import { addDays, getTime, startOfWeek } from "date-fns";
import { useEffect, useState } from "react";
import { HttpError } from "../../../api/types";
import useAuthStore from "../../../store/auth";
import useSettingsStore from "../../../store/settings";
import { isAdmin } from "../../../utils/roles";
import { Week } from "../types";

const DEFAULT_WEEK_IN_THE_PAST = 0;
const DEFAULT_WEEK_IN_THE_FUTURE = 4;

/**
 * Custom hook to fetch weeks
 * @returns {object} - Contains weeks, loading state, error state, and refetch function.
 */
function useFetchWeeks() {
  const [settings] = useSettingsStore(state => [state.settings]);
  const [user] = useAuthStore(state => [state.user]);

  const [weeks, setWeeks] = useState<Week[]>([]);
  const [currentWeek, setCurrentWeek] = useState<Week>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<HttpError | null>(null);

  const fetchWeeks = async () => {
    setLoading(true);
    setError(null);

    try {
      const weeks: Week[] = [];

      const start = startOfWeek(new Date(), { weekStartsOn: 1 });
      const end = addDays(start, 6);

      let weeksInThePast = 0;
      if (isAdmin(user)) {
        // only admins can see the past weeks
        const weeksInThePastSetting = settings?.weeks_history_number ?? DEFAULT_WEEK_IN_THE_PAST;
        weeksInThePast = Number(weeksInThePastSetting);
      }

      const weeksInTheFutureSetting = settings?.weeks_number ?? DEFAULT_WEEK_IN_THE_FUTURE;
      const weeksInTheFuture = Number(weeksInTheFutureSetting);

      for (let i = -weeksInThePast; i < weeksInTheFuture; i++) {
        weeks.push({
          id: getTime(addDays(start, i * 7)),
          start: addDays(start, i * 7),
          end: addDays(end, i * 7),
        });
        if (i === 0) {
          setCurrentWeek(weeks.at(-1));
        }
      }

      setWeeks(weeks);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWeeks();
  }, [user]);

  return { weeks, currentWeek, loading, error, refetchWeeks: fetchWeeks };
}

export default useFetchWeeks;
